import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Search, Filter, Package, Tag, Printer, Briefcase, Gift, Shield } from 'lucide-react';

const ProductsSolutions = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    document.title = 'Products & Solutions | Multicolor International';
  }, []);

  const categories = [
    { id: 'all', name: 'All Products', icon: Package },
    { id: 'labels', name: 'Labels & Stickers', icon: Tag },
    { id: 'printing', name: 'Printing Services', icon: Printer },
    { id: 'corporate', name: 'Corporate Products', icon: Briefcase },
    { id: 'promotional', name: 'Promotional Items', icon: Gift },
    { id: 'security', name: 'Security Solutions', icon: Shield }
  ];

  const products = [
    {
      category: 'printing',
      title: 'Screen & Offset Printing',
      description: 'High-quality commercial printing for all your business needs',
      image: 'https://images.unsplash.com/photo-1599930113854-d6d7fd522504?w=600&q=80&auto=format',
      industries: ['Corporate', 'Retail', 'FMCG'],
      applications: ['Business cards', 'Brochures', 'Flyers', 'Posters'],
      materials: ['Paper', 'Cardboard', 'Vinyl']
    },
    {
      category: 'labels',
      title: 'Crystal & Coated Labels',
      description: 'Premium labels with superior finish and durability',
      image: 'https://images.unsplash.com/photo-1607344645866-009c320b63e0?w=600&q=80&auto=format',
      industries: ['Food & Beverage', 'Manufacturing', 'Retail'],
      applications: ['Product labeling', 'Branding', 'Packaging'],
      materials: ['Crystal film', 'Coated paper', 'Vinyl']
    },
    {
      category: 'labels',
      title: 'Vinyl & Mylar Stickers',
      description: 'Durable stickers for indoor and outdoor applications',
      image: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&q=80&auto=format',
      industries: ['Retail', 'Manufacturing', 'Automotive'],
      applications: ['Product stickers', 'Promotional materials', 'Vehicle graphics'],
      materials: ['Vinyl', 'Mylar', 'Reflective film']
    },
    {
      category: 'corporate',
      title: 'Plastic Photo ID Cards',
      description: 'Professional ID cards for staff, students, and members',
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&q=80&auto=format',
      industries: ['Corporate', 'Education', 'Healthcare'],
      applications: ['Employee IDs', 'Student cards', 'Membership cards'],
      materials: ['PVC', 'Polycarbonate']
    },
    {
      category: 'labels',
      title: 'Industrial Name Plates',
      description: 'Durable metal and plastic name plates for industrial use',
      image: 'https://images.unsplash.com/photo-1581092334651-ddf26d9a09d0?w=600&q=80&auto=format',
      industries: ['Manufacturing', 'Industrial', 'Automotive'],
      applications: ['Equipment labeling', 'Safety signs', 'Asset tags'],
      materials: ['Aluminum', 'Stainless steel', 'Plastic']
    },
    {
      category: 'promotional',
      title: 'Promotional T-Shirts & Caps',
      description: 'Custom apparel printing for events and branding',
      image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&q=80&auto=format',
      industries: ['Corporate', 'Events', 'Retail'],
      applications: ['Team uniforms', 'Event merchandise', 'Promotional wear'],
      materials: ['Cotton', 'Polyester', 'Blends']
    },
    {
      category: 'promotional',
      title: 'Corporate Merchandise',
      description: 'Mugs, pens, wall clocks, and other promotional items',
      image: 'https://images.unsplash.com/photo-1572119865084-43c285814d63?w=600&q=80&auto=format',
      industries: ['Corporate', 'Events', 'Marketing'],
      applications: ['Corporate gifts', 'Event giveaways', 'Marketing campaigns'],
      materials: ['Ceramic', 'Plastic', 'Metal']
    },
    {
      category: 'security',
      title: 'Hologram Security Seals',
      description: 'High-security holographic seals for product authentication',
      image: 'https://images.unsplash.com/photo-1614036417651-efe5912149d8?w=600&q=80&auto=format',
      industries: ['Pharmaceutical', 'Electronics', 'Luxury goods'],
      applications: ['Product authentication', 'Anti-counterfeit', 'Warranty seals'],
      materials: ['Holographic film', 'Security paper']
    },
    {
      category: 'corporate',
      title: 'Metallic Mounts & Coat Pins',
      description: 'Premium metallic products for corporate use',
      image: 'https://images.unsplash.com/photo-1589998059171-988d887df646?w=600&q=80&auto=format',
      industries: ['Corporate', 'Events', 'Retail'],
      applications: ['Corporate gifts', 'Award plaques', 'Badges'],
      materials: ['Brass', 'Aluminum', 'Stainless steel']
    },
    {
      category: 'labels',
      title: 'Barcode Solutions',
      description: 'Complete barcode generation and printing services',
      image: 'https://images.unsplash.com/photo-1607349913338-fca6f7fc42d0?w=600&q=80&auto=format',
      industries: ['Retail', 'Logistics', 'Manufacturing'],
      applications: ['Inventory management', 'Product tracking', 'Asset management'],
      materials: ['Thermal paper', 'Synthetic labels']
    },
    {
      category: 'printing',
      title: 'Outdoor Signage',
      description: 'Large-format printing for outdoor advertising',
      image: 'https://images.unsplash.com/photo-1568952433726-3896e3881c65?w=600&q=80&auto=format',
      industries: ['Advertising', 'Retail', 'Real Estate'],
      applications: ['Billboards', 'Banners', 'Vehicle wraps'],
      materials: ['Vinyl', 'Flex', 'Mesh']
    },
    {
      category: 'promotional',
      title: 'Event Branding Materials',
      description: 'Complete branding solutions for events and exhibitions',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80&auto=format',
      industries: ['Events', 'Marketing', 'Corporate'],
      applications: ['Booth graphics', 'Standees', 'Backdrops'],
      materials: ['Fabric', 'Vinyl', 'PVC']
    }
  ];

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold mb-6" data-testid="products-heading">
              Products &
              <br />
              <span className="gradient-text">Solutions</span>
            </h1>
            <p className="text-xl text-[#5B5B5B] max-w-3xl mx-auto">
              Comprehensive printing, packaging, and branding solutions for every industry
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-12 bg-white sticky top-20 z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B3A6D] focus:border-transparent"
                data-testid="product-search-input"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {categories.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium whitespace-nowrap transition ${
                      selectedCategory === category.id
                        ? 'bg-[#1B3A6D] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`category-filter-${category.id}`}
                  >
                    <Icon size={18} />
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 text-sm text-[#5B5B5B]">
            Showing {filteredProducts.length} of {products.length} products
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow"
                data-testid={`product-${product.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="relative h-64 overflow-hidden bg-gradient-to-br from-[#1B3A6D]/10 to-[#3FABF3]/10">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform hover:scale-110 duration-500"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute top-4 right-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-[#1B3A6D]">
                    {categories.find(c => c.id === product.category)?.name}
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-bold mb-2">{product.title}</h3>
                    <p className="text-[#5B5B5B] text-sm">{product.description}</p>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
                      Industries
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {product.industries.map((industry) => (
                        <span
                          key={industry}
                          className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-700"
                        >
                          {industry}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
                      Applications
                    </h4>
                    <ul className="space-y-1">
                      {product.applications.slice(0, 3).map((app) => (
                        <li key={app} className="text-xs text-[#5B5B5B] flex items-center">
                          <span className="w-1.5 h-1.5 bg-[#00A651] rounded-full mr-2" />
                          {app}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    className="w-full btn-secondary text-sm py-2"
                    data-testid={`request-quote-${product.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    Request Quote
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need a Custom Solution?
            </h2>
            <p className="text-lg text-[#5B5B5B] mb-8">
              Our team can develop tailored printing and branding solutions to meet your specific requirements.
            </p>
            <button className="btn-primary" data-testid="custom-solution-cta">
              Discuss Your Project
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsSolutions;